import React from 'react';
import { X, CheckCircle, AlertTriangle, ShieldCheck, Wrench, Activity } from 'lucide-react';

/**
 * Results modal for the "🔧 Diagnose & Repair" Bank-Health orchestrator.
 *
 * Renders the consolidated report returned from
 * ``POST /api/bank/health/diagnose-and-repair``:
 *
 *   { ok, all_green, fixed: [...], needs_manual: [...],
 *     details: { running_balances, mirror_backfill,
 *                smart_engine_cleanup, orphan_scan, drift_snapshot } }
 *
 * Purpose:
 *   Replace a generic toast with a clear, scannable status panel so the
 *   CFO knows exactly which historical issues were auto-healed and
 *   which (if any) still need manual attention.
 */
const num = (v) =>
  v == null
    ? '—'
    : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Section = ({ title, icon: Icon, tone = 'slate', children }) => {
  const toneCls = {
    slate:  'border-slate-200 bg-slate-50',
    green:  'border-green-200 bg-green-50',
    amber:  'border-amber-200 bg-amber-50',
    red:    'border-red-200 bg-red-50',
    indigo: 'border-indigo-200 bg-indigo-50',
  }[tone];
  return (
    <div className={`rounded-lg border p-3 ${toneCls}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-slate-700" />}
        <h4 className="font-semibold text-sm text-slate-900">{title}</h4>
      </div>
      {children}
    </div>
  );
};

export const DiagnoseRepairResultModal = ({ report, onClose }) => {
  if (!report) return null;
  const { all_green, fixed = [], needs_manual = [], details = {} } = report;
  const rb     = details.running_balances     || {};
  const mb     = details.mirror_backfill      || {};
  const se     = details.smart_engine_cleanup || {};
  const orphan = details.orphan_scan          || {};
  const drift  = Array.isArray(details.drift_snapshot) ? details.drift_snapshot : [];

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="diagnose-repair-result-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div
          className={`p-5 flex items-start justify-between ${
            all_green
              ? 'bg-gradient-to-r from-green-600 to-emerald-600'
              : needs_manual.length === 0
              ? 'bg-gradient-to-r from-indigo-600 to-purple-600'
              : 'bg-gradient-to-r from-amber-600 to-orange-600'
          } text-white`}
        >
          <div className="flex items-center gap-3">
            {all_green ? (
              <ShieldCheck className="w-8 h-8" />
            ) : (
              <Wrench className="w-8 h-8" />
            )}
            <div>
              <h3 className="text-lg font-bold">
                {all_green
                  ? 'النِظام سَليم تَماماً'
                  : needs_manual.length === 0
                  ? `تَم إِصلاح ${fixed.length} مَشاكِل تِلقائياً`
                  : `إِصلاح: ${fixed.length} · يَحتاج تَدَخُّل: ${needs_manual.length}`}
              </h3>
              <p className="text-xs opacity-90 mt-0.5">
                تَقرير Diagnose &amp; Repair الشامِل
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="diagnose-repair-close-btn"
            className="hover:bg-white/20 rounded-lg p-1.5 transition-colors"
            aria-label="إِغلاق"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto">
          {/* All-green banner */}
          {all_green && (
            <div
              className="rounded-lg border border-green-300 bg-green-50 p-4 flex items-center gap-3"
              data-testid="diagnose-repair-all-green"
            >
              <CheckCircle className="w-6 h-6 text-green-600 shrink-0" />
              <div className="text-sm text-green-900">
                لا تُوجَد مَشاكِل. كُل الأرصِدة والقُيود والمَرايا مُتَطابِقَة. GL ≡ Σ Tx ≡ Stored.
              </div>
            </div>
          )}

          {/* Fixed list */}
          {fixed.length > 0 && (
            <Section title={`تَم إِصلاحه تِلقائياً (${fixed.length})`} icon={CheckCircle} tone="green">
              <ul
                className="space-y-1.5 text-sm text-green-900"
                data-testid="diagnose-repair-fixed-list"
              >
                {fixed.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Needs manual */}
          {needs_manual.length > 0 && (
            <Section
              title={`يَحتاج تَدَخُّل يَدَوي (${needs_manual.length})`}
              icon={AlertTriangle}
              tone="amber"
            >
              <ul
                className="space-y-1.5 text-sm text-amber-900"
                data-testid="diagnose-repair-needs-manual-list"
              >
                {needs_manual.map((m, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Statistics grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="أرصِدَة مُصحَّحَة" value={(rb.fixed_accounts || []).length} tone="indigo" />
            <Stat label="Mirrors مُولَّدَة" value={mb.created || 0} tone="indigo" />
            <Stat label="رَوابِط Smart يَتيمَة" value={(se.links_removed || 0) + (se.matches_removed || 0)} tone="indigo" />
            <Stat label="سِجِلَّات يَتيمَة" value={orphan.total || 0} tone={orphan.total ? 'amber' : 'slate'} />
          </div>

          {/* Drift snapshot */}
          {drift.length > 0 && (
            <Section title="لَقطَة Drift النِهائية (Stored vs Σ Tx)" icon={Activity} tone="slate">
              <div className="overflow-x-auto -mx-1">
                <table
                  className="w-full text-xs"
                  data-testid="diagnose-repair-drift-table"
                >
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-1.5 px-2">البَنك</th>
                      <th className="py-1.5 px-2 text-right">Stored</th>
                      <th className="py-1.5 px-2 text-right">Σ Tx</th>
                      <th className="py-1.5 px-2 text-right">Drift</th>
                      <th className="py-1.5 px-2 text-center">الحالة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drift.map((d, i) => {
                      const red = d.color === 'red';
                      return (
                        <tr
                          key={i}
                          className={`border-b border-slate-100 ${red ? 'bg-red-50' : ''}`}
                        >
                          <td className="py-1.5 px-2 font-medium text-slate-900">{d.bank_name}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{num(d.stored)}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{num(d.sigma_tx)}</td>
                          <td className={`py-1.5 px-2 text-right font-mono ${red ? 'text-red-700 font-bold' : 'text-slate-500'}`}>
                            {num(d.drift_vs_tx)}
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <span
                              className={`inline-block w-2 h-2 rounded-full ${
                                red ? 'bg-red-500' : 'bg-green-500'
                              }`}
                            />
                            <span className={`ms-1.5 text-[10px] font-bold uppercase ${red ? 'text-red-700' : 'text-green-700'}`}>
                              {red ? 'RED' : 'OK'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* Running-balance fixes detail */}
          {(rb.fixed_accounts || []).length > 0 && (
            <Section title="تَفاصيل تَصحيح الأرصدة" icon={Wrench} tone="indigo">
              <div className="overflow-x-auto -mx-1">
                <table className="w-full text-xs" data-testid="diagnose-repair-rb-table">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-1.5 px-2">البَنك</th>
                      <th className="py-1.5 px-2 text-right">القيمَة القَديمَة</th>
                      <th className="py-1.5 px-2 text-right">القيمَة الجَديدَة</th>
                      <th className="py-1.5 px-2 text-right">الفَرق</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rb.fixed_accounts.map((a, i) => {
                      const diff = (a.new || 0) - (a.old || 0);
                      return (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-1.5 px-2 font-medium text-slate-900">{a.bank_name}</td>
                          <td className="py-1.5 px-2 text-right font-mono text-slate-500">{num(a.old)}</td>
                          <td className="py-1.5 px-2 text-right font-mono text-slate-900">{num(a.new)}</td>
                          <td
                            className={`py-1.5 px-2 text-right font-mono font-semibold ${
                              diff < 0 ? 'text-red-700' : diff > 0 ? 'text-green-700' : 'text-slate-500'
                            }`}
                          >
                            {diff > 0 ? '+' : ''}
                            {num(diff)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 flex justify-end bg-slate-50">
          <button
            onClick={onClose}
            data-testid="diagnose-repair-footer-close"
            className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-medium transition-colors"
          >
            إِغلاق
          </button>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, tone = 'slate' }) => {
  const cls = {
    slate:  'border-slate-200 bg-white text-slate-700',
    indigo: 'border-indigo-200 bg-indigo-50 text-indigo-900',
    amber:  'border-amber-200 bg-amber-50 text-amber-900',
    green:  'border-green-200 bg-green-50 text-green-900',
  }[tone];
  return (
    <div className={`rounded-lg border p-2.5 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-75">{label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
};

export default DiagnoseRepairResultModal;
